import { col, type Shape } from './base.ts';

export const VFM_PREFERRED_RATES: Shape = [
  col('averageOilRate', 'liquidRate', 'Preferred VFM method average oil rate.', { suffix: 'AverageOilRate' }),
  col('averageAssociatedGasRate', 'gasRate', 'Preferred VFM method average associated gas rate.', {
    suffix: 'AverageAssociatedGasRate',
  }),
  col('averageNonAssociatedGasRate', 'gasRate', 'Preferred VFM method average non-associated gas rate.', {
    suffix: 'AverageNonAssociatedGasRate',
  }),
  col('averageWaterRate', 'liquidRate', 'Preferred VFM method average water rate.', { suffix: 'AverageWaterRate' }),
  col('estimatedWellOilVolume', 'liquidVolume', 'Estimated well oil volume from the preferred VFM method.', {
    suffix: 'EstimatedWellOilVolume',
  }),
  col('estimatedWellWaterVolume', 'liquidVolume', 'Estimated well water volume from the preferred VFM method.', {
    suffix: 'EstimatedWellWaterVolume',
  }),
  col('dataSourceFlag', 'unitless', 'Which VFM method was selected as preferred.', { suffix: 'DataSourceFlag' }),
];

/**
 * Weighted-average-VFM-method rates + volume on
 * `AllocatedRates_VFM_String_Agent`. The family mixes TWO source
 * prefixes — `weightedAverageVFM` for the rates/volume but bare
 * `weightedAverage` for the data-source flag (`weightedAverageDataSourceFlag`,
 * no `VFM` segment) — so no single prefix covers it. Authored verbatim with
 * `prefix:''` (suffix === source column). Unlike the preferred family this has
 * only the oil volume; there is no weighted estimated water volume.
 */
export const VFM_WEIGHTED_RATES: Shape = [
  col('averageOilRate', 'liquidRate', 'Weighted average oil rate across all VFM methods.', {
    suffix: 'weightedAverageVFMOilRate',
  }),
  col('averageAssociatedGasRate', 'gasRate', 'Weighted average associated gas rate across VFM methods.', {
    suffix: 'weightedAverageVFMAssociatedGasRate',
  }),
  col('averageNonAssociatedGasRate', 'gasRate', 'Weighted average non-associated gas rate across VFM methods.', {
    suffix: 'weightedAverageVFMNonAssociatedGasRate',
  }),
  col('averageWaterRate', 'liquidRate', 'Weighted average water rate across VFM methods.', {
    suffix: 'weightedAverageVFMWaterRate',
  }),
  col('estimatedWellOilVolume', 'liquidVolume', 'Estimated well oil volume from the weighted average method.', {
    suffix: 'weightedAverageVFMEstimatedWellOilVolume',
  }),
  col('dataSourceFlag', 'unitless', 'Data source flag for the weighted average method.', {
    suffix: 'weightedAverageDataSourceFlag',
  }),
];

/**
 * Per-method average oil-rate estimates on `AllocatedRates_VFM_String_Agent`
 * NOT a clean method-prefix axis: each method contributes exactly ONE
 * oil-rate column with an irregular infix (`SystemAnalysis` vs bare
 * `IPR`/`VLP`/`Choke`/`ESP`/`DataDriven`), all sharing the `vfm` prefix and the
 * `AverageOilRate` tail. Authored verbatim with `prefix:''` as a single flat
 * shape rather than six single-column method views.
 */
export const VFM_METHOD_OIL_RATES: Shape = [
  col('systemAnalysisAverageOilRate', 'liquidRate', 'System-analysis average oil rate.', {
    suffix: 'vfmSystemAnalysisAverageOilRate',
  }),
  col('iprAverageOilRate', 'liquidRate', 'IPR-analysis average oil rate.', { suffix: 'vfmIPRAverageOilRate' }),
  col('vlpAverageOilRate', 'liquidRate', 'VLP-analysis average oil rate.', { suffix: 'vfmVLPAverageOilRate' }),
  col('chokeAverageOilRate', 'liquidRate', 'Choke-analysis average oil rate.', { suffix: 'vfmChokeAverageOilRate' }),
  col('espAverageOilRate', 'liquidRate', 'ESP-analysis average oil rate.', { suffix: 'vfmESPAverageOilRate' }),
  col('dataDrivenAverageOilRate', 'liquidRate', 'Data-driven model average oil rate.', {
    suffix: 'vfmDataDrivenAverageOilRate',
  }),
];

/**
 * Allocated phase rates on `AllocatedRates_VFM_String_Agent`: the final
 * allocated rate per phase from BOTH the preferred and the weighted-average
 * methods. Two irregular source sub-prefixes coexist — preferred is uniform
 * `vfmPreferredMethod*`, but the weighted oil column carries an extra `Method`
 * segment (`vfmWeightedAverageMethodOilAllocationRate`) the weighted gas/water
 * columns lack (`vfmWeightedAverage*AllocationRate`) — so no single prefix
 * covers all four weighted columns. Authored verbatim with `prefix:''` and
 * preferred/weighted-distinguished canonicals so both families live in one view.
 */
export const VFM_ALLOCATED_RATES: Shape = [
  col('preferredOilAllocationRate', 'liquidRate', 'Allocated oil rate from the preferred VFM method.', {
    suffix: 'vfmPreferredMethodOilAllocationRate',
  }),
  col(
    'preferredAssociatedGasAllocationRate',
    'gasRate',
    'Allocated associated gas rate from the preferred VFM method.',
    { suffix: 'vfmPreferredMethodAssociatedGasAllocationRate' },
  ),
  col(
    'preferredNonAssociatedGasAllocationRate',
    'gasRate',
    'Allocated non-associated gas rate from the preferred VFM method.',
    { suffix: 'vfmPreferredMethodNonAssociatedGasAllocationRate' },
  ),
  col('preferredWaterAllocationRate', 'liquidRate', 'Allocated water rate from the preferred VFM method.', {
    suffix: 'vfmPreferredMethodWaterAllocationRate',
  }),
  col('weightedOilAllocationRate', 'liquidRate', 'Allocated oil rate from the weighted average method.', {
    suffix: 'vfmWeightedAverageMethodOilAllocationRate',
  }),
  col(
    'weightedAssociatedGasAllocationRate',
    'gasRate',
    'Allocated associated gas rate from the weighted average method.',
    { suffix: 'vfmWeightedAverageAssociatedGasAllocationRate' },
  ),
  col(
    'weightedNonAssociatedGasAllocationRate',
    'gasRate',
    'Allocated non-associated gas rate from the weighted average method.',
    { suffix: 'vfmWeightedAverageNonAssociatedGasAllocationRate' },
  ),
  col('weightedWaterAllocationRate', 'liquidRate', 'Allocated water rate from the weighted average method.', {
    suffix: 'vfmWeightedAverageWaterAllocationRate',
  }),
];

/**
 * Un-prefixed shared allocation context on `AllocatedRates_VFM_String_Agent`
 * Injection rates, allocation workflow status and well uptime. These
 * exist as bare source columns (no method prefix), authored verbatim with
 * `prefix:''` (suffix === source column).
 */
export const VFM_ALLOCATION_CONTEXT: Shape = [
  col('averageWaterInjectionRate', 'liquidRate', 'Average water injection rate.'),
  col('averageGasInjectionRate', 'gasRate', 'Average gas injection rate.'),
  col('oilProductionAllocationStatus', 'unitless', 'Oil allocation workflow status.'),
  col('wellUptime', 'unitless', 'Well uptime hours in the allocation period.'),
];
/**
 * PTS well-test rates at LINE conditions (the `Pts1` sample) on
 * `PtsOutputs_String_Agent`. The sample marker is a TRAILING suffix
 * (`…Pts1`), not a leading prefix, so each column's `suffix` carries the full
 * verbatim source tail and `canonical` is the de-marked name; applied with the
 * default empty prefix. Casing is irregular at the source — `wellTestGOR`
 * (all-caps) carries the `Pts1` marker as `wellTestGORPts1`, while the SC/Ref
 * samples spell it `wellTestGor…`.
 */
